import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarX, X } from "lucide-react";
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, format, isSameMonth, isSameDay, addMonths, subMonths, parseISO } from "date-fns";
import { es } from "date-fns/locale";

import CalendarEventCard from "./CalendarEventCard";

import styles from "./calendar-view.module.scss";

const WEEK_DAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const MAX_VISIBLE_EVENTS_PER_DAY = 3;

const safeParseDate = (value) => {
	if (!value) return null;
	if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
	const parsed = typeof value === "string" ? parseISO(value) : new Date(value);
	return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? parsed : null;
};

const buildCalendarDays = (referenceMonth) => {
	const monthStart = startOfMonth(referenceMonth);
	const monthEnd = endOfMonth(referenceMonth);
	const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
	const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
	return eachDayOfInterval({ start: gridStart, end: gridEnd });
};

const groupEventsByDay = (data, dateAccessor) => {
	const groupedByDay = new Map();
	data.forEach((row) => {
		const dateValue = safeParseDate(row?.[dateAccessor]);
		if (!dateValue) return;
		const dayKey = format(dateValue, "yyyy-MM-dd");
		if (!groupedByDay.has(dayKey)) {
			groupedByDay.set(dayKey, []);
		}
		groupedByDay.get(dayKey).push(row);
	});
	return groupedByDay;
};

const CalendarView = ({
	data = [],
	dateAccessor = "application_order_date",
	eventConfig = {},
	onEventClick,
	renderEvent,
	onMonthChange,
	emptyMessage = "No hay registros con fecha para mostrar.",
}) => {
	const [currentMonth, setCurrentMonth] = useState(() => new Date());
	const [selectedDayKey, setSelectedDayKey] = useState(null);

	const calendarDays = useMemo(() => buildCalendarDays(currentMonth), [currentMonth]);
	const eventsByDay = useMemo(() => groupEventsByDay(data, dateAccessor), [data, dateAccessor]);

	const monthLabel = format(currentMonth, "MMMM yyyy", { locale: es });
	const today = new Date();

	const renderItem = (eventItem) => {
		if (typeof renderEvent === "function") {
			return renderEvent(eventItem);
		}
		return <CalendarEventCard record={eventItem} eventConfig={eventConfig} />;
	};

	const totalEventsInMonth = useMemo(() => {
		let total = 0;
		calendarDays.forEach((dayDate) => {
			if (!isSameMonth(dayDate, currentMonth)) return;
			const dayKey = format(dayDate, "yyyy-MM-dd");
			total += eventsByDay.get(dayKey)?.length || 0;
		});
		return total;
	}, [calendarDays, currentMonth, eventsByDay]);

	const handleEventClick = (eventItem) => {
		if (typeof onEventClick === "function") onEventClick(eventItem);
	};

	const changeCurrentMonth = (nextMonthOrResolver) => {
		setCurrentMonth((previousMonth) => {
			const nextMonth = typeof nextMonthOrResolver === "function" ? nextMonthOrResolver(previousMonth) : nextMonthOrResolver;
			if (typeof onMonthChange === "function") {
				onMonthChange(nextMonth);
			}
			return nextMonth;
		});
	};

	const closeDayDetail = () => setSelectedDayKey(null);

	const selectedDayEvents = selectedDayKey ? eventsByDay.get(selectedDayKey) || [] : [];

	return (
		<div className={styles.calendarWrapper}>
			<div className={styles.calendarToolbar}>
				<div className={styles.toolbarLeft}>
					<button
						type="button"
						className={styles.navButton}
						onClick={() => changeCurrentMonth((current) => subMonths(current, 1))}
						aria-label="Mes anterior"
					>
						<ChevronLeft size={18} />
					</button>
					<button type="button" className={styles.todayButton} onClick={() => changeCurrentMonth(new Date())}>
						Hoy
					</button>
					<button
						type="button"
						className={styles.navButton}
						onClick={() => changeCurrentMonth((current) => addMonths(current, 1))}
						aria-label="Mes siguiente"
					>
						<ChevronRight size={18} />
					</button>
				</div>
				<div className={styles.monthLabel}>{monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1)}</div>
				<div className={styles.toolbarRight}>
					<span className={styles.totalChip}>
						{totalEventsInMonth} {totalEventsInMonth === 1 ? "registro" : "registros"}
					</span>
				</div>
			</div>

			<div className={styles.weekHeader}>
				{WEEK_DAYS.map((dayName) => (
					<div key={dayName} className={styles.weekHeaderCell}>
						{dayName}
					</div>
				))}
			</div>

			<div className={styles.daysGrid}>
				{calendarDays.map((dayDate) => {
					const dayKey = format(dayDate, "yyyy-MM-dd");
					const eventsInDay = eventsByDay.get(dayKey) || [];
					const isCurrentMonth = isSameMonth(dayDate, currentMonth);
					const isToday = isSameDay(dayDate, today);
					const visibleEvents = eventsInDay.slice(0, MAX_VISIBLE_EVENTS_PER_DAY);
					const remainingCount = eventsInDay.length - visibleEvents.length;

					return (
						<div key={dayKey} className={`${styles.dayCell} ${isCurrentMonth ? "" : styles.dayCellOutside} ${isToday ? styles.dayCellToday : ""}`}>
							<div className={styles.dayNumberRow}>
								<span className={styles.dayNumber}>{format(dayDate, "d")}</span>
								{eventsInDay.length > 0 && <span className={styles.dayCount}>{eventsInDay.length}</span>}
							</div>
							<div className={styles.dayEvents}>
								{visibleEvents.map((eventItem, eventIndex) => (
									<button
										key={`${dayKey}-${eventItem?.id || eventItem?.code || eventIndex}`}
										type="button"
										className={styles.eventButton}
										onClick={() => handleEventClick(eventItem)}
										title="Ver detalle"
									>
										{renderItem(eventItem)}
									</button>
								))}
								{remainingCount > 0 && (
									<button type="button" className={styles.moreEventsButton} onClick={() => setSelectedDayKey(dayKey)}>
										+{remainingCount} más
									</button>
								)}
							</div>
						</div>
					);
				})}
			</div>

			{data.length === 0 && (
				<div className={styles.emptyMessage}>
					<CalendarX size={28} />
					<span>{emptyMessage}</span>
				</div>
			)}

			{selectedDayKey && selectedDayEvents.length > 0 && (
				<div className={styles.dayDetailBackdrop}>
					<div className={styles.dayDetail}>
						<div className={styles.dayDetailHeader}>
							<span className={styles.dayDetailTitle}>{format(parseISO(selectedDayKey), "EEEE d 'de' MMMM yyyy", { locale: es })}</span>
							<button type="button" className={styles.dayDetailClose} onClick={closeDayDetail} aria-label="Cerrar">
								<X size={18} />
							</button>
						</div>
						<div className={styles.dayDetailList}>
							{selectedDayEvents.map((eventItem, eventIndex) => (
								<button
									key={`detail-${selectedDayKey}-${eventItem?.id || eventIndex}`}
									type="button"
									className={styles.eventButton}
									onClick={() => {
										handleEventClick(eventItem);
										closeDayDetail();
									}}
								>
									{renderItem(eventItem)}
								</button>
							))}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default CalendarView;
